<template>
	<main :aria-label="mainHeading">
		<InformationBar v-if="informationBar" :blok="informationBar" />
		<div class="space-y-section">
			<Breadcrumbs :items="breadcrumbs" class="container text-body-tertiary" />

			<LazyPageHeadingWithHypermarket
				class="container !mb-6 !mt-10"
				:heading="mainHeading"
				information-text="Informace o&nbsp;produktech se vztahují k&nbsp;pobočce"
				:default-hm="DEFAULT_HYPERMARKET"
				show-links-in-selection-banner
			/>

			<!-- When no hypermarket is selected, a fallback is used in SSR and products are rendered for better SEO -->
			<!-- However, we want to hide the fallback products with v-show in the browser -->
			<div class="mt-(--section-gap-xs)!">
				<ExpandTransition aria-label="Celá nabídka kategorie">
					<div v-if="nestedCategories.length" :key="currentCategory?.categoryId" class="overflow-hidden">
						<div class="container" :class="{'opacity-50 pointer-events-none': pending}">
							<ProductCategoryStack :categories="nestedCategories" :route-generator="routeGenerator" is-product-catalog />
						</div>
					</div>
				</ExpandTransition>

				<ProductListing
					:current-category-id="currentCategory?.categoryId"
					:category-name="currentCategory?.categoryName ?? null"
					@pending="pending = $event"
				/>
			</div>

			<div class="container space-y-[var(--section-gap)]">
				<Click :placement="AdPosition.ACTION_OFFER_PODVAL" />
			</div>
		</div>
	</main>
</template>

<script setup lang="ts">
	import {last} from '@dovca/toolbox';
	import {storeToRefs} from 'pinia';
	import {useGtm} from '@gtm-support/vue-gtm';
	import type {RouteLocationRaw} from 'vue-router';
	import {AdPosition} from '~/openapi';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import type {BreadcrumbItem} from '@/types/breadcrumbs';
	import {useProductCatalogStore, fullCatalogVirtualCategories} from '~/stores/productCatalog';
	import type {Gsoa_ProductCatalog_Eshop_ProductCategoryEshop as ProductCategory} from '~/openapi';
	import type {Tree} from '~/types/utils';
	import {DEFAULT_HYPERMARKET} from '#shared/constants';

	definePageMeta({
		key: 'productCatalog',
		title: 'Celá nabídka | Globus',
	});

	const pending = ref(false);

	const gtm = useGtm();

	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarket, selectedHypermarketInstance} = storeToRefs(hypermarketsStore);

	const productCatalogStore = useProductCatalogStore();
	await useAsyncData('productCatalogCategories', () => productCatalogStore.fetchCategories());
	const {categoryTree} = storeToRefs(productCatalogStore);

	const category = useVariadicRouteParam('category');

	const informationBar = computed(() => {
		const bar = selectedHypermarketInstance.value?.content.informationBar![0];
		return bar && bar.visibleOnPages?.includes('PageTypeActionOfferHomepage') ? bar : null;
	});

	const categoriesFromRouteSlugs = computed<Tree<ProductCategory>[]>(() => {
		if (!categoryTree.value || !category.value) {
			return [];
		}

		return category.value?.reduce((parents, slug) => {
			const parent = last(parents);

			// Check if slug matches a virtual category
			const virtualCategory = Object.values(fullCatalogVirtualCategories).find(
				(cat) => getProductCategorySlug(cat as ProductCategory) === slug,
			);

			if (virtualCategory) {
				// Create a tree node for the virtual category
				return [...parents, {
					item: virtualCategory as ProductCategory,
					children: [],
				}];
			}

			const child = parent?.children.find(({item}) => getProductCategorySlug(item) === slug);

			// Invalid category hierarchy
			if (!child) {
				throw createError({statusCode: 404});
			}

			return [...parents, child];
		}, [categoryTree.value]) ?? [];
	});

	const currentCategoryTree = computed<Tree<ProductCategory> | undefined>(() => last(categoriesFromRouteSlugs.value));

	const currentCategory = computed<ProductCategory | undefined>(() => currentCategoryTree.value?.item);

	const isProductCatalogHomepage = computed<boolean>(() => category.value.length === 0);

	const nestedCategories = computed<ProductCategory[]>(() => (
		currentCategoryTree.value?.children?.map(({item}) => item) ?? []
	));

	const branchSpecificPath = useBranchSpecificPath();

	const breadcrumbs = computed<BreadcrumbItem[]>(() => {
		const firstItems: BreadcrumbItem[] = [
			{
				id: 0,
				parent_id: null,
				slug: selectedHypermarket.value ?? 'globus',
				name: selectedHypermarketInstance.value?.content.name || 'Globus',
			},
			{
				id: 1,
				parent_id: 0,
				slug: branchSpecificPath({name: 'hypermarket-cela-nabidka-category'}).value,
				name: 'Celá nabídka',
			},
		];

		return [
			...firstItems,
			...mapWithPreviousMapped(
				// Exclude the root category (it is represented by `firstItems`)
				categoriesFromRouteSlugs.value.slice(1),
				(previous, {item}) => ({
					id: previous.id + 1,
					parent_id: previous.id,
					slug: branchSpecificPath({name: 'hypermarket-cela-nabidka-category', params: {category: getProductCategorySlug(item)}}).value,
					name: item.categoryName || '',
				}),
				firstItems[firstItems.length - 1],
			),
		];
	});

	const mainHeading = computed<string>(() => {
		const genericHeading = 'Celá nabídka';

		return isProductCatalogHomepage.value
			? genericHeading
			: currentCategory.value?.categoryName || genericHeading;
	});

	function routeGenerator(cat: ProductCategory): RouteLocationRaw {
		const parents = categoriesFromRouteSlugs.value.slice(1).map(({item}) => item);

		return branchSpecificPath({
			name: 'hypermarket-cela-nabidka-category',
			params: {category: [...parents, cat].map(getProductCategorySlug)},
		}).value;
	}

	if (import.meta.client && window?.dataLayer) {
		watch(mainHeading, async (newHeading) => {
			await nextTick();
			setTimeout(() => {
				gtm?.push({
					event: 'gtm_route_middleware_dynamic',
					content_group: newHeading,
				});
			}, 0);
		}, {immediate: true});
	}

	useMetaTitle(`${mainHeading.value} | Globus`);
</script>
